import React, { useCallback, useEffect, useState } from "react";

import {
  Card,
  Typography,
  Skeleton,
  Tooltip,
} from "antd";

import {
  ApartmentOutlined,
  BookOutlined,
  ClusterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import { motion } from "framer-motion";

import api from "../../api/api";

const { Text } = Typography;

const AcademicCount = ({ theme }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [counts, setCounts] = useState({
    total_grade_classes: 0,
    total_subjects: 0,
    total_divisions: 0,
  });

  const fetchCounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const res = await api.get(
        "/grade-class-subject-division-count/"
      );

      setCounts({
        total_grade_classes: Number(
          res.data?.total_grade_classes || 0
        ),
        total_subjects: Number(
          res.data?.total_subjects || 0
        ),
        total_divisions: Number(
          res.data?.total_divisions || 0
        ),
      });
    } catch (error) {
      console.error(
        "Failed to fetch academic counts:",
        error
      );
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();

    const updateCounts = () => {
      fetchCounts();
    };

    window.addEventListener(
      "academicCountUpdated",
      updateCounts
    );

    window.addEventListener(
      "academic-year-updated",
      updateCounts
    );

    return () => {
      window.removeEventListener(
        "academicCountUpdated",
        updateCounts
      );

      window.removeEventListener(
        "academic-year-updated",
        updateCounts
      );
    };
  }, [fetchCounts]);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ height: "100%" }}
      >
        <Card
          bordered={false}
          style={{
            height: "100%",
            minHeight: 218,
            borderRadius: 22,
            background: theme.card,
            border: `1px solid ${theme.border}`,
            boxShadow:
              "0 14px 40px rgba(6,42,75,.07)",
          }}
          styles={{
            body: {
              padding: 22,
            },
          }}
        >
          <Skeleton
            active
            paragraph={{ rows: 4 }}
          />
        </Card>
      </motion.div>
    );
  }

  const metrics = [
    {
      label: "Courses",
      value: counts.total_subjects,
      icon: <BookOutlined />,
      color: "#0F6DAB",
      background:
        theme.mode === "dark"
          ? "rgba(15,109,171,.12)"
          : "#F3F8FC",
    },
    {
      label: "Departments",
      value: counts.total_divisions,
      icon: <ApartmentOutlined />,
      color: "#C0750D",
      background:
        theme.mode === "dark"
          ? "rgba(192,117,13,.12)"
          : "#FCF8F1",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{
        y: -5,
        transition: { duration: 0.2 },
      }}
      style={{ height: "100%" }}
    >
      <Card
        bordered={false}
        style={{
          height: "100%",
          minHeight: 218,
          borderRadius: 22,
          overflow: "hidden",
          position: "relative",
          background: theme.card,
          border: `1px solid ${theme.border}`,
          boxShadow:
            "0 14px 40px rgba(6,42,75,.07)",
        }}
        styles={{
          body: {
            padding: 0,
            height: "100%",
          },
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: 4,
            background:
              "linear-gradient(90deg,#062A4B,#5F6622,#4FAE22)",
          }}
        />

        <div
          style={{
            padding: 22,
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 13,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg,#062A4B,#5F6622)",
                  color: "#fff",
                  fontSize: 20,
                  boxShadow:
                    "0 7px 18px rgba(6,42,75,.18)",
                }}
              >
                <ClusterOutlined />
              </div>

              <div>
                <Text
                  style={{
                    display: "block",
                    color: theme.text,
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  Academic Structure
                </Text>

                <Text
                  style={{
                    color: theme.secondary,
                    fontSize: 11,
                  }}
                >
                  Current institutional setup
                </Text>
              </div>
            </div>

            <Tooltip title="Refresh">
              <button
                type="button"
                onClick={fetchCounts}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                  background: theme.soft,
                  color: theme.secondary,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReloadOutlined />
              </button>
            </Tooltip>
          </div>

          <div
            style={{
              marginTop: 17,
              display: "flex",
              alignItems: "baseline",
              gap: 7,
            }}
          >
            <span
              style={{
                fontSize: 38,
                lineHeight: 1,
                fontWeight: 800,
                letterSpacing: "-1.5px",
                color: theme.text,
              }}
            >
              {error
                ? "—"
                : counts.total_grade_classes.toLocaleString()}
            </span>

            <Text
              style={{
                color: theme.secondary,
                fontSize: 12,
              }}
            >
              Classes Or Levels
            </Text>
          </div>

          <Text
            style={{
              display: "block",
              marginTop: 6,
              color: theme.secondary,
              fontSize: 12,
            }}
          >
            Structured academic units currently available
          </Text>

          {error ? (
            <button
              type="button"
              onClick={fetchCounts}
              style={{
                marginTop: 18,
                border: 0,
                background: "transparent",
                padding: 0,
                color: "#C0750D",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Unable to load. Try again
            </button>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginTop: 18,
              }}
            >
              {metrics.map((item) => (
                <div
                  key={item.label}
                  style={{
                    padding: "11px 12px",
                    borderRadius: 14,
                    background: item.background,
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                    }}
                  >
                    <span
                      style={{
                        color: item.color,
                        fontSize: 14,
                      }}
                    >
                      {item.icon}
                    </span>

                    <Text
                      style={{
                        color: theme.secondary,
                        fontSize: 11,
                      }}
                    >
                      {item.label}
                    </Text>
                  </div>

                  <strong
                    style={{
                      display: "block",
                      marginTop: 4,
                      color: theme.text,
                      fontSize: 18,
                    }}
                  >
                    {item.value.toLocaleString()}
                  </strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

export default AcademicCount;